import { useMemo } from "react";
import { useTellescopeQuery } from "../../use-tellescope-query";
import { buildIntegrationSyncMap, type IntegrationSyncMap } from "./integration-sync-helpers";
import { useAuth } from "../../../auth";

/**
 * Loads the org's (redacted) integrations and reduces them into a per-model
 * auto-sync summary via {@link buildIntegrationSyncMap}.
 *
 * Feature-gating call sites can combine this with plan availability to decide
 * whether a model action will actually auto-sync.
 *
 * @returns
 *   - `syncMap`: an {@link IntegrationSyncMap} keyed only by models some
 *     integration affects (absent keys ⇒ no integration touches the model).
 *   - `isLoading`: `true` while integrations are still loading.
 */
export const useIntegrationSyncMap = (): { syncMap: IntegrationSyncMap; isLoading: boolean } => {
  const { isAuthenticated } = useAuth();
  const { data, isLoading } = useTellescopeQuery({
    model: "integrations",
    method: "load_redacted",
    request: {},
    enabled: !!isAuthenticated,
  });

  const syncMap = useMemo(() => buildIntegrationSyncMap(data?.integrations ?? []), [data]);

  return { syncMap, isLoading };
};
